/**
 * Welcome-call wizard (2.3.1): availability windows and days, language and
 * gender requirements, caregiver constraints, SMS consent. Everything it
 * captures is a requirement: there is no hard-versus-soft control, because there
 * is no soft class (script author, discussion turn 67). SMS consent is here as
 * the fact the confirmation round reads, not dressed as a constraint.
 * The call stays a human judgment; the same fields can be populated by a call
 * center or voice agent and reviewed here. Output is structured constraints on
 * the patient that cascade to every visit (2.3.2), mirrored to the note.
 */
import { Fragment, useMemo, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { coordinationNoteMirror, parseDays, summarizeConstraints } from "../engine/constraints";
import { patientConstraintsUpdated, welcomeCallCompleted } from "../presenter/actions";
import type { PatientConstraint } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoSelector } from "../store/useDemoStore";
import { useDemoBranchClock } from "./branchClock";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEK = [1, 2, 3, 4, 5, 6, 7];
/** The branch's own day. Any day ticked at all is available for all of it. */
const DAY_OPEN = "08:00";
const DAY_CLOSE = "18:00";
/** "" is no preference, and it is the default: most patients ask for nothing. */
const LANGUAGES = ["English", "Spanish", "Vietnamese", "Cantonese", "Russian", "Somali", "Other"];
const STEPS = ["Availability", "Clinician traits", "Caregiver & consent", "Review"];

export interface WelcomeCallWizardProps {
  open: boolean;
  onClose: () => void;
  patientRef: string;
  /** welcomeCall flips the readiness gate (and converts FFS holds); edit only updates the constraints. */
  mode: "welcomeCall" | "edit";
}

/**
 * **AM and PM per day, and one window in the store.** The grid a person fills in
 * has fourteen cells; a `PatientConstraint` has a day list and a single window
 * that applies to all of them, and it is too late in the demo to change that
 * (Pete, 2026-09-22). So a day is available when either half of it is ticked,
 * and any day ticked at all stores the branch's own 08:00–18:00. Reopening the
 * wizard ticks both halves of every available day, which is the honest reading
 * of what was saved.
 */
interface Draft {
  am: number[];
  pm: number[];
  language: string;
  gender: "" | "F" | "M";
  caregiver: string;
  smsConsent: "yes" | "no";
}

function draftFrom(constraints: PatientConstraint[]): Draft {
  const find = (kind: PatientConstraint["kind"]) => constraints.find((c) => c.kind === kind);
  const days = find("availabilityDays");
  const lang = find("language");
  const gender = find("gender");
  const care = find("caregiver");
  const sms = find("smsConsent");
  return {
    am: days ? parseDays(days.value) : [...WEEK],
    pm: days ? parseDays(days.value) : [...WEEK],
    language: lang?.value ?? "",
    gender: (gender?.value as "F" | "M" | undefined) ?? "",
    caregiver: care?.value ?? "",
    smsConsent: sms?.value === "no" ? "no" : "yes",
  };
}

export function constraintsFrom(d: Draft): PatientConstraint[] {
  const out: PatientConstraint[] = [];
  const open = WEEK.filter((i) => d.am.includes(i) || d.pm.includes(i));
  if (open.length) {
    out.push({ kind: "availabilityDays", value: open.map((i) => DAYS[i - 1]).join(",") });
    out.push({ kind: "availabilityWindow", value: `${DAY_OPEN}-${DAY_CLOSE}` });
  }
  if (d.language) out.push({ kind: "language", value: d.language });
  if (d.gender) out.push({ kind: "gender", value: d.gender });
  if (d.caregiver.trim()) out.push({ kind: "caregiver", value: d.caregiver.trim() });
  out.push({ kind: "smsConsent", value: d.smsConsent });
  return out;
}

export function WelcomeCallWizard({ open, onClose, patientRef, mode }: WelcomeCallWizardProps) {
  const patient = useDemoSelector((s) => s.patients[patientRef]);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(patient?.constraints ?? []));
  const [busy, setBusy] = useState(false);
  const constraints = useMemo(() => constraintsFrom(draft), [draft]);
  const clock = useDemoBranchClock();
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const toggleHalf = (half: "am" | "pm", i: number) => {
    const on = draft[half];
    set({ [half]: on.includes(i) ? on.filter((d) => d !== i) : [...on, i].sort((a, b) => a - b) } as Partial<Draft>);
  };

  const complete = async () => {
    setBusy(true);
    try {
      if (mode === "welcomeCall") await welcomeCallCompleted(patientRef, constraints);
      else patientConstraintsUpdated(patientRef, constraints);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {mode === "welcomeCall" ? "Welcome call" : "Scheduling constraints"} · {patient?.displayName}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* The week as a grid: a column per day, a row for each half of it, and
            everything ticked to begin with — a welcome call asks what the patient
            cannot do, not what they can (Pete, 2026-09-22). */}
        {step === 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: `auto repeat(7, minmax(0, 1fr))`, columnGap: 0.5, rowGap: 0.5, alignItems: "center" }}>
            <Box />
            {DAYS.map((d) => (
              <Typography key={d} variant="body2" fontWeight={600} textAlign="center">
                {d}
              </Typography>
            ))}
            {(["am", "pm"] as const).map((half) => (
              <Fragment key={half}>
                <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                  {half.toUpperCase()}
                </Typography>
                {WEEK.map((i) => (
                  <Box key={i} sx={{ textAlign: "center" }}>
                    <Checkbox size="small" checked={draft[half].includes(i)} onChange={() => toggleHalf(half, i)} inputProps={{ "aria-label": `${DAYS[i - 1]} ${half.toUpperCase()}` }} />
                  </Box>
                ))}
              </Fragment>
            ))}
          </Box>
        )}

        {/* **Clinician traits**: the names in one column and the controls in the
            other, so the two read as one list rather than as two unrelated
            fields (Pete, 2026-09-22). Both default to no preference, which is
            what most patients ask for; a language only becomes a requirement
            when somebody picks one. */}
        {step === 1 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 3, rowGap: 2, alignItems: "center" }}>
            <Typography variant="body2">Language</Typography>
            <TextField size="small" select value={draft.language} onChange={(e) => set({ language: e.target.value })} sx={{ minWidth: 200 }}>
              <MenuItem value="">No preference</MenuItem>
              {LANGUAGES.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2">Gender</Typography>
            <RadioGroup row value={draft.gender} onChange={(e) => set({ gender: e.target.value as Draft["gender"] })}>
              <FormControlLabel value="" control={<Radio size="small" />} label="No preference" />
              <FormControlLabel value="F" control={<Radio size="small" />} label="Female" />
              <FormControlLabel value="M" control={<Radio size="small" />} label="Male" />
            </RadioGroup>
          </Box>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <TextField size="small" multiline minRows={2} label="Caregiver / access" placeholder="e.g. Daughter present mornings; use the side door" value={draft.caregiver} onChange={(e) => set({ caregiver: e.target.value })} />
            <Box>
              <Typography variant="body2" gutterBottom>
                SMS consent
              </Typography>
              <RadioGroup row value={draft.smsConsent} onChange={(e) => set({ smsConsent: e.target.value as Draft["smsConsent"] })}>
                <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio size="small" />} label="No — voice only" />
              </RadioGroup>
            </Box>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1.5}>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {summarizeConstraints(constraints).map((sentence) => clock.sentence(sentence)).map((line) => (
                <li key={line}>
                  <Typography variant="body2">{line}</Typography>
                </li>
              ))}
            </Box>
            <Box sx={{ p: 1, bgcolor: "#fafafa", border: "1px dashed #ddd", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Coordination note
              </Typography>
              <Typography variant="body2">{clock.sentence(coordinationNoteMirror(constraints))}</Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || busy}>
          Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)}>
            Next
          </Button>
        ) : (
          <Button variant="contained" color="success" onClick={complete} disabled={busy}>
            {mode === "welcomeCall" ? "Complete call" : "Save constraints"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
